import {
  AnswerScale,
  Disposition,
  QuestionType,
  ShowWhenParentAnswer,
  UnderwritingApproach,
  UnderwritingQuestionSetQuery,
} from '@coverright/data-access/types/medigap';

// Shared, UI-agnostic logic for the Medigap carrier underwriting questionnaire - see
// medigap-underwriting-frontend-integration.md. The quoting panel (agent-quoting) and the manager's
// question-catalog editor (admin settings) render very different controls but agree on these rules,
// so they live here rather than being duplicated per app.

type QuestionSet = NonNullable<UnderwritingQuestionSetQuery['underwritingQuestionSet']>;

// The generated type nests `children` only as deep as the query selects, which makes the recursive
// shape awkward to pass around. Every level carries the same fields, so we widen to one self-
// referential type that the renderers can walk without caring how deep the document went.
export type UnderwritingQuestion = Omit<QuestionSet['questions'][number], 'children'> & {
  children?: UnderwritingQuestion[];
};

/**
 * One node's captured response. `answer` is the Yes/No(/Not sure) value; `note` is the agent's
 * free text, which can be present with or without an answer. Both ride in the same
 * saveUnderwritingAnswers entry.
 */
export type UnderwritingAnswerValue = { answer?: string; note?: string };

/** Responses keyed by question id. Absent means untouched. */
export type UnderwritingAnswerMap = Record<string, UnderwritingAnswerValue | undefined>;

export const ANSWER_YES = 'YES';
export const ANSWER_NO = 'NO';
export const ANSWER_NOT_SURE = 'NOT_SURE';

/** A question may override the set-level scale; fall back to the set's default when it doesn't. */
export const resolveAnswerScale = (
  question: Pick<UnderwritingQuestion, 'answerScale'>,
  setScale: AnswerScale,
): AnswerScale => question.answerScale ?? setScale;

export const answerOptions = (scale: AnswerScale): { value: string; label: string }[] =>
  scale === AnswerScale.YesNoNotSure
    ? [
        { value: ANSWER_YES, label: 'Yes' },
        { value: ANSWER_NO, label: 'No' },
        { value: ANSWER_NOT_SURE, label: 'Not sure' },
      ]
    : [
        { value: ANSWER_YES, label: 'Yes' },
        { value: ANSWER_NO, label: 'No' },
      ];

/**
 * Conditional display: a child node is shown only when the parent's current answer matches its
 * `showWhenParentAnswer`. Top-level nodes have no parent, so they are always shown.
 */
export const isQuestionVisible = (
  question: Pick<UnderwritingQuestion, 'showWhenParentAnswer'>,
  parentAnswer: string | undefined,
  hasParent: boolean,
): boolean => {
  if (!hasParent || question.showWhenParentAnswer === ShowWhenParentAnswer.Always) return true;
  if (question.showWhenParentAnswer === ShowWhenParentAnswer.Yes) return parentAnswer === ANSWER_YES;
  if (question.showWhenParentAnswer === ShowWhenParentAnswer.No) return parentAnswer === ANSWER_NO;
  return true;
};

/**
 * `answerable` alone decides whether a node takes input - deliberately NOT gated on
 * `type === QUESTION`. Sub-items are answerable nodes too (Yes/No + note, stored per questionId
 * like any other), so gating on type would silently swallow them.
 *
 * SECTION and NOTE are structural, so they never take input regardless of the flag.
 */
export const isAnswerable = (question: Pick<UnderwritingQuestion, 'type' | 'answerable'>): boolean =>
  question.answerable && question.type !== QuestionType.Section && question.type !== QuestionType.Note;

export const DISPOSITION_META: Record<Disposition, { label: string; color: string; bg: string } | null> = {
  [Disposition.Disqualifying]: { label: 'Disqualifying', color: '#B42318', bg: '#FEF3F2' },
  [Disposition.UnderwritingReview]: { label: 'Underwriting review', color: '#B54708', bg: '#FFFAEB' },
  [Disposition.NonDisqualifying]: { label: 'Non-disqualifying', color: '#027A48', bg: '#ECFDF3' },
  [Disposition.Informational]: { label: 'Informational', color: '#344054', bg: '#F2F4F7' },
  // NONE means "no eligibility meaning" - render no badge at all rather than a neutral one.
  [Disposition.None]: null,
};

export const UNDERWRITING_APPROACH_LABEL: Record<UnderwritingApproach, string> = {
  [UnderwritingApproach.UnderwrittenUnlessOeGi]: 'Underwritten unless OE/GI',
  [UnderwritingApproach.GuaranteedIssue]: 'Guaranteed issue',
  [UnderwritingApproach.Other]: 'Other',
};

export const QUESTION_TYPE_LABEL: Record<QuestionType, string> = {
  [QuestionType.Section]: 'Section',
  [QuestionType.Question]: 'Question',
  [QuestionType.Subitem]: 'Sub-item',
  [QuestionType.Note]: 'Note',
};

/**
 * Walks the visible tree and collects every answerable question the agent should currently see.
 * Used to decide completion state and to build the save payload, so that answers belonging to
 * branches that are currently hidden (parent flipped from Yes to No) aren't submitted.
 */
export const collectVisibleAnswerable = (
  questions: UnderwritingQuestion[],
  answers: UnderwritingAnswerMap,
  parentAnswer?: string,
  hasParent = false,
): UnderwritingQuestion[] => {
  const result: UnderwritingQuestion[] = [];
  for (const question of questions) {
    if (!isQuestionVisible(question, parentAnswer, hasParent)) continue;
    if (isAnswerable(question)) result.push(question);
    if (question.children?.length) {
      result.push(...collectVisibleAnswerable(question.children, answers, answers[question.id]?.answer, true));
    }
  }
  return result;
};

/** Sibling ordering. The backend already applies `displayOrder`, but re-sorting keeps it stable. */
export const byDisplayOrder = <T extends { displayOrder: number }>(a: T, b: T) => a.displayOrder - b.displayOrder;
